import React from 'react';

const ff = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif";

function pct(p) { return `${Math.round((p || 0) * 100)}%`; }
function pct1(p) { return `${((p || 0) * 100).toFixed(1)}%`; }
function k(v) { return v >= 1000 ? `${(v / 1000).toFixed(2)}k` : Math.round(v); }

// Outcome odds bar
function OddsRow({ label, value, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ fontSize: 12, color: '#6E6E73', width: 110, fontFamily: ff }}>{label}</div>
      <div style={{ flex: 1, height: 8, background: '#F0F0F2', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct(value), background: color, borderRadius: 4, transition: 'width 0.5s cubic-bezier(0.16,1,0.3,1)' }} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#1D1D1F', width: 52, textAlign: 'right', fontFamily: ff }}>{pct1(value)}</div>
    </div>
  );
}

// Range bar: shows p10–p90 span with median marker. Worst/Median/Best.
function RangeStat({ label, unit, p10, p25, p50, p75, p90, domainMin, domainMax, fmt = v => Math.round(v) }) {
  const span = domainMax - domainMin || 1;
  const x = v => `${Math.max(0, Math.min(100, ((v - domainMin) / span) * 100))}%`;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1D1D1F', fontFamily: ff }}>{label}</div>
        <div style={{ fontSize: 12, color: '#86868B', fontFamily: ff }}>median <strong style={{ color: '#1D1D1F' }}>{fmt(p50)}{unit}</strong></div>
      </div>
      <div style={{ position: 'relative', height: 28 }}>
        {/* full p10–p90 track */}
        <div style={{ position: 'absolute', top: 11, left: x(p10), width: `calc(${x(p90)} - ${x(p10)})`, height: 6, background: '#E5E5EA', borderRadius: 3 }} />
        {/* p25–p75 inner */}
        <div style={{ position: 'absolute', top: 11, left: x(p25), width: `calc(${x(p75)} - ${x(p25)})`, height: 6, background: '#C7C7CC', borderRadius: 3 }} />
        {/* median marker */}
        <div style={{ position: 'absolute', top: 6, left: x(p50), width: 3, height: 16, background: '#1D1D1F', borderRadius: 2, transform: 'translateX(-1.5px)' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontFamily: ff }}>
        <div><span style={{ color: '#c0392b', fontWeight: 700 }}>{fmt(p10)}{unit}</span> <span style={{ color: '#86868B' }}>floor</span></div>
        <div style={{ textAlign: 'right' }}><span style={{ color: '#1a9640', fontWeight: 700 }}>{fmt(p90)}{unit}</span> <span style={{ color: '#86868B' }}>ceiling</span></div>
      </div>
    </div>
  );
}

// Win distribution mini-histogram
function WinBars({ histogram, playoffCutline }) {
  if (!histogram?.length) return null;
  const max = Math.max(...histogram.map(h => h.prob), 0.01);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 70 }}>
        {histogram.map(h => {
          const inP = h.wins >= playoffCutline;
          return (
            <div key={h.wins} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
              <div title={`${h.wins} wins: ${pct(h.prob)}`} style={{
                width: '100%', height: `${(h.prob / max) * 100}%`, minHeight: h.prob > 0 ? 2 : 0,
                background: inP ? 'rgba(52,199,89,0.7)' : '#D2D2D7', borderRadius: '2px 2px 0 0',
              }} />
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: '#C7C7CC', fontFamily: ff }}>
        <span>0 wins</span><span style={{ color: 'rgba(52,199,89,0.7)' }}>green = playoff range</span><span>{histogram.length - 1}</span>
      </div>
    </div>
  );
}

export default function TeamDetailModal({ team, result, bootstrap, rank, isMe, onClose }) {
  if (!team || !result) return null;

  const weeks = bootstrap.regularSeasonWeeks || 14;
  const playoffCutline = Math.round(weeks * (bootstrap.playoffTeams || 6) / (bootstrap.teams.length || 12));

  // Domains for range bars
  const allMaxPF = bootstrap.teams.map(t => bootstrap._maxPFRange).filter(Boolean);
  const mpfP10 = result.maxPointsFor?.p10 || 0;
  const mpfP90 = result.maxPointsFor?.p90 || 0;
  const mpfPad = (mpfP90 - mpfP10) * 0.3 + 20;

  const winsP10 = result.wins?.p10 ?? 0;
  const winsP90 = result.wins?.p90 ?? weeks;

  const ord = rank ? `${rank}${['st','nd','rd'][rank - 1] || 'th'}` : '';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        fontFamily: ff,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#FFFFFF', borderRadius: 20, width: '100%', maxWidth: 460,
          maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '22px 24px 18px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 12 }}>
          {team.avatarUrl
            ? <img src={team.avatarUrl} alt="" style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0 }} />
            : <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#F5F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#86868B', flexShrink: 0 }}>{(team.displayName || '?')[0]}</div>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {team.displayName}{isMe && <span style={{ fontSize: 10, fontWeight: 700, background: '#1D1D1F', color: '#fff', borderRadius: 4, padding: '2px 6px', marginLeft: 8, verticalAlign: 'middle' }}>YOU</span>}
            </div>
            <div style={{ fontSize: 12, color: '#86868B', marginTop: 2 }}>
              {ord && `Projected ${ord} · `}{ord && 'finish'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#C7C7CC', fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: '0 2px' }}>×</button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Outcome odds */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#86868B', marginBottom: 12 }}>How the season ends</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <OddsRow label="Make playoffs" value={result.playoffProbability} color="#FF9500" />
              <OddsRow label="Reach the final" value={result.finalsProbability || 0} color="#5AC8FA" />
              <OddsRow label="Win championship" value={result.championshipProbability} color="#34C759" />
            </div>
          </div>

          {/* Win range */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#86868B', marginBottom: 14 }}>Final record — range across {(bootstrap.teams.length, '')} sims</div>
            <RangeStat
              label="Wins" unit=""
              p10={result.wins?.p10} p25={result.wins?.p25} p50={result.wins?.p50} p75={result.wins?.p75} p90={result.wins?.p90}
              domainMin={Math.max(0, winsP10 - 1)} domainMax={Math.min(weeks, winsP90 + 1)}
              fmt={v => (v ?? 0).toFixed(0)}
            />
            <div style={{ fontSize: 12, color: '#6E6E73', marginTop: 10, lineHeight: 1.5 }}>
              In 80% of simulations, {shortTeam(team.displayName)} finishes between{' '}
              <strong style={{ color: '#1D1D1F' }}>{(result.wins?.p10 ?? 0).toFixed(0)}</strong> and{' '}
              <strong style={{ color: '#1D1D1F' }}>{(result.wins?.p90 ?? 0).toFixed(0)} wins</strong>.
            </div>
          </div>

          {/* Max PF range */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#86868B', marginBottom: 14 }}>Season scoring (max points for)</div>
            <RangeStat
              label="Total points" unit=""
              p10={result.maxPointsFor?.p10} p25={result.maxPointsFor?.p25} p50={result.maxPointsFor?.p50} p75={result.maxPointsFor?.p75} p90={result.maxPointsFor?.p90}
              domainMin={mpfP10 - mpfPad} domainMax={mpfP90 + mpfPad}
              fmt={v => k(v)}
            />
          </div>

          {/* Win distribution */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#86868B', marginBottom: 12 }}>Win distribution</div>
            <WinBars histogram={result.winHistogram} playoffCutline={playoffCutline} />
          </div>
        </div>
      </div>
    </div>
  );
}

function shortTeam(name) {
  if (!name) return 'this team';
  return name.length > 18 ? name.slice(0, 17) + '…' : name;
}
